import { apiClient } from './client';
import type { BaseResponse } from '@/types/api';

// ── Types ──

export interface UserRow {
  username: string;
  role: 'admin' | 'user' | 'ops' | 'demo';
  owner: string | null;
  is_active: boolean;
  expires_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface StationOwnerRow {
  station_label: string;
  owner: string | null;
}

export interface AuditLogRow {
  id: number;
  timestamp: string;
  username: string;
  action: string;
  target: string | null;
  detail: string | null;
}

// ── User CRUD ──

export async function fetchUsers(): Promise<UserRow[]> {
  const res = await apiClient.get<BaseResponse<UserRow[]>>('/admin/users');
  return res.data.data;
}

export async function createUser(data: {
  username: string;
  password: string;
  role: string;
  owner: string | null;
  expires_at: string | null;
}): Promise<void> {
  await apiClient.post('/admin/users', data);
}

export async function updateUser(
  username: string,
  data: {
    role?: string;
    owner?: string | null;
    is_active?: boolean;
    expires_at?: string | null;
  }
): Promise<void> {
  await apiClient.put(`/admin/users/${encodeURIComponent(username)}`, data);
}

export function toExpiryIso(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error('Invalid account expiry');
  }
  return parsed.toISOString();
}

export function toExpiryInputValue(value?: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return [
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
  ].join('T');
}

export async function deleteUser(username: string): Promise<void> {
  await apiClient.delete(`/admin/users/${encodeURIComponent(username)}`);
}

// ── Station Owners ──

export async function fetchStationOwners(): Promise<StationOwnerRow[]> {
  const res = await apiClient.get<BaseResponse<StationOwnerRow[]>>('/admin/station-owners');
  return res.data.data;
}

export async function updateStationOwner(
  stationLabel: string,
  owner: string
): Promise<void> {
  await apiClient.put(
    `/admin/station-owners/${encodeURIComponent(stationLabel)}`,
    { owner }
  );
}

export async function deleteStation(stationLabel: string): Promise<void> {
  await apiClient.delete(`/admin/station-owners/${encodeURIComponent(stationLabel)}`);
}

// ── Audit Log ──

export interface AuditLogResponse {
  data: AuditLogRow[];
  total: number;
  page: number;
  page_size: number;
}

interface AuditLogRawResponse extends BaseResponse<AuditLogRow[]> {
  total: number;
  page: number;
  page_size: number;
}

export async function fetchAuditLog(params: {
  page?: number;
  page_size?: number;
  action?: string;
  username?: string;
}): Promise<AuditLogResponse> {
  const res = await apiClient.get<AuditLogRawResponse>('/admin/audit-log', { params });
  return {
    data: res.data.data,
    total: res.data.total,
    page: res.data.page,
    page_size: res.data.page_size,
  };
}

// ── Self-service ──

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await apiClient.post('/me/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

// ── Business Account ──

export interface CreateStationItem {
  station_label: string;
  pv_capacity?: number | null;
  bat_p?: number | null;
  bat_c?: number | null;
  subsidy_price?: number | null;
}

export interface BusinessAccountResult {
  view_username: string;
  ops_username: string;
  stations_created: string[];
}

export async function createBusinessAccount(data: {
  owner_id: string;
  password: string;
  stations?: CreateStationItem[];
}): Promise<BusinessAccountResult> {
  const res = await apiClient.post<BaseResponse<BusinessAccountResult>>(
    '/admin/users/business',
    data
  );
  return res.data.data;
}
